import { useEffect, useState, useMemo } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/lib/supabase';
import { AppGrid } from '@/components/AppGrid';
import { OverviewProgressCharts, MIN_SESSIONS_FOR_CHARTS } from '@/components/ProgressCharts';
import type { LessonSession } from '@/components/ProgressCharts';
import { TOKO_APPS, getAppById } from '@/config/apps';
import { calculateSessionStats } from '@/utils/sessionStats';
import { Pagination } from '@/components/Pagination';
import styles from './AppProgress.module.css';

const ITEMS_PER_PAGE = 10;

export function Progress() {
  const { user } = useAuth();
  const [sessions, setSessions] = useState<LessonSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);

  const enabledApps = useMemo(() => {
    const appNames = new Set(sessions.map(s => s.app_name));
    return Array.from(appNames);
  }, [sessions]);

  const linkOverrides = useMemo(() => {
    const overrides: Record<string, string> = {};
    TOKO_APPS.forEach(app => {
      overrides[app.id] = `/progress/${app.id}`;
    });
    return overrides;
  }, []);

  const stats = useMemo(() => calculateSessionStats(sessions), [sessions]);

  // Calculate pagination for sessions
  const totalPages = Math.ceil(sessions.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const paginatedSessions = sessions.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }

    const userId = user.id;

    async function fetchSessions() {
      const { data, error } = await supabase
        .from('lesson_sessions')
        .select('*')
        .eq('user_id', userId)
        .order('completed_at', { ascending: false });

      if (error) {
        setError(error.message);
      } else {
        setSessions(data || []);
      }
      setLoading(false);
    }

    fetchSessions();
  }, [user]);

  if (!user) {
    return (
      <div className={styles.container}>
        <h1>Progress</h1>
        <p>Please sign in to view progress.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className={styles.container}>
        <h1>Progress</h1>
        <p>Loading...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.container}>
        <h1>Progress</h1>
        <p className={styles.error}>Error: {error}</p>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <h1>Progress</h1>
      <AppGrid enabledApps={enabledApps} linkOverrides={linkOverrides} disabledClickable />

      {sessions.length >= MIN_SESSIONS_FOR_CHARTS && (
        <section className={styles.section}>
          <h2>Statistics</h2>
          <OverviewProgressCharts sessions={sessions} isLoading={loading} />
        </section>
      )}

      {stats ? (
        <>
          <section className={styles.section}>
            <h2>Overview</h2>
            <div className={styles.statsGrid}>
              <div className={styles.statCard}>
                <span className={styles.statValue}>{stats.totalSessions}</span>
                <span className={styles.statLabel}>Total Sessions</span>
              </div>
              <div className={styles.statCard}>
                <span className={styles.statValue}>{stats.passRate.toFixed(0)}%</span>
                <span className={styles.statLabel}>Pass Rate</span>
              </div>
              <div className={styles.statCard}>
                <span className={styles.statValue}>{stats.averageAccuracy.toFixed(1)}%</span>
                <span className={styles.statLabel}>Avg Accuracy</span>
              </div>
              <div className={styles.statCard}>
                <span className={styles.statValue}>{stats.averageTime.toFixed(1)}s</span>
                <span className={styles.statLabel}>Avg Time/Card</span>
              </div>
            </div>
          </section>

          <section className={styles.section}>
            <h2>Recent Sessions</h2>
            <div className={styles.sessionsList}>
              {paginatedSessions.map(session => {
                const app = getAppById(session.app_name);
                return (
                  <div key={session.id} className={styles.sessionCard}>
                    <div className={styles.sessionHeader}>
                      <span className={styles.sessionMode}>
                        {app?.shortName ?? session.app_name}
                        {' - '}
                        {session.practice_mode === 'schedule'
                          ? `Schedule ${session.schedule} Unit ${session.unit_number}`
                          : 'Free Play'}
                      </span>
                      <span className={`${styles.sessionStatus} ${session.passed ? styles.passed : styles.failed}`}>
                        {session.passed ? 'Passed' : 'Failed'}
                      </span>
                    </div>
                    <div className={styles.sessionStats}>
                      <span>Accuracy: {session.accuracy.toFixed(1)}%</span>
                      <span>Time: {(session.total_time / 1000).toFixed(1)}s / {session.goal_time}s</span>
                      <span>Correct: {session.correct_count}/{session.total_cards}</span>
                    </div>
                    <div className={styles.sessionDate}>
                      {new Date(session.completed_at).toLocaleDateString()} at{' '}
                      {new Date(session.completed_at).toLocaleTimeString()}
                    </div>
                  </div>
                );
              })}
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
              />
            </div>
          </section>
        </>
      ) : (
        <section className={styles.section}>
          <p className={styles.noData}>No sessions recorded yet.</p>
          <p>Start practicing to see your progress here!</p>
        </section>
      )}

      <section className={styles.section}>
        <h2>Raw Data</h2>
        <details>
          <summary>Session Data ({sessions.length} sessions)</summary>
          <pre className={styles.rawData}>
            {JSON.stringify(sessions, null, 2)}
          </pre>
        </details>
      </section>
    </div>
  );
}
